/**
 * Streamed text, held until a line is complete, then played back a whole word at a time on a steady
 * beat.
 *
 * Measured on 2026-09-13: Gemini Flash through Vertex hands a five-sentence answer over as four
 * chunks of 86, 157, 177 and 23 characters inside 0.4 seconds. Recorded runs of Gemini, Grok and
 * DeepSeek live in `__tests__/fixtures/streams` (DECK-1690).
 *
 * The look is Kobra's streaming text, measured on its live demo (DECK-1692): one word per 60 Hz frame,
 * no bursts, no pauses, each word fading in over a 180 ms that overlaps the next ten. A real wire
 * cannot feed that beat: Gemini sends "Creating the" and then nothing for 8 seconds, Grok sends a
 * sentence in bursts. Playing whatever has arrived shows a frozen fragment or a line that runs dry
 * word by word. So nothing partial is shown (Guri):
 * - **Hold.** A line waits, unseen, until it is complete ({@link completeLength}): its text ends a
 *   sentence (. : ! ?) or a paragraph (newline), or the stream sealed it (`textBoundary`, `done`, the
 *   block settling). While it waits, the reveal reports {@link TextReveal.holding}, so the thread can
 *   show that the agent is still thinking.
 * - **Play.** A complete line plays one whole word per {@link BEAT_MS}, exactly: one a frame at 60 Hz,
 *   one every other frame at 120 Hz. It never stretches and never stops mid-line.
 * - **Long paragraphs.** One still being written may start early once {@link EARLY_WORDS} whole words
 *   are waiting. Only such a paragraph may stretch when it runs low (a word every two beats, then
 *   three, never slower than {@link MAX_WORD_MS}), and wait if the wire goes quiet.
 *
 * `LiveProse` fades each new word in; this file only decides which words are on screen. When the turn
 * settles (`live` false) the text snaps to its full length, so nothing animates on a history render.
 */
import { useEffect, useRef, useState } from 'react';

/** One word per beat: a 60 Hz frame, the rate Kobra's demo plays at. */
export const BEAT_MS = 1000 / 60;
/** A paragraph still being written may start playing once this many whole words are waiting. */
export const EARLY_WORDS = 20;
/** Fewer whole words than this waiting, in a paragraph that started early, stretches the beat. */
export const LOW_WORDS = 3;
/** The slowest a stretched beat gets: never longer than this between two words. */
export const MAX_WORD_MS = 50;

const isSpace = (char: string | undefined) => char !== undefined && /\s/.test(char);

/** Sentence punctuation, any closing marks after it, then whitespace or the end of the text. */
const SENTENCE_END = /[.:!?][)\]"'’”*_`]*(?=\s|$)/g;

/**
 * How much of `text` is complete: all of it once sealed, otherwise everything through its last
 * sentence end (. : ! ? and any closing marks) or its last newline. A dot or colon straight after a
 * digit at the very end waits for what follows, because "9." may become "9.5" and "2:" may be "2:30".
 */
export function completeLength(text: string, sealed: boolean): number {
  if (sealed) return text.length;
  let complete = text.lastIndexOf('\n') + 1;
  for (const match of text.matchAll(SENTENCE_END)) {
    const at = match.index ?? 0;
    const end = at + match[0].length;
    const number = end === text.length && /^[.:]$/.test(match[0]) && /\d/.test(text[at - 1] ?? '');
    if (!number) complete = Math.max(complete, end);
  }
  return complete;
}

/**
 * The playback of one growing text, on an injected clock. Pure, so the beat is tested frame by frame
 * without React: `arrive` when the text grows, `seal` when the stream closes it, `frame` once per
 * animation frame.
 */
export class TextReveal {
  private target: string;
  /** Characters on screen: 0, the end of a whole word, or all of it. */
  private end = 0;
  private sealed = false;
  /** This paragraph started before it was complete, so it plays whole words as they come. */
  private early = false;
  /** Beats elapsed toward the next word. Full while holding, so a line's first word goes at once. */
  private beats = MAX_WORD_MS / BEAT_MS;
  private lastFrameAt: number;

  /** The line's first text, arrived at `nowMs`. `onScreen` starts caught up, for text already showing. */
  constructor(target: string, nowMs: number, { onScreen = false }: { onScreen?: boolean } = {}) {
    this.target = target;
    this.lastFrameAt = nowMs;
    if (onScreen) this.end = target.length;
  }

  get shown(): string {
    return this.target.slice(0, this.end);
  }

  /** Nothing but whitespace is left to show, so the frame loop can rest until the next arrival. */
  get caughtUp(): boolean {
    return this.target.slice(this.end).trim() === '';
  }

  /** Text is waiting and none of it may show yet: the line is not complete. */
  get holding(): boolean {
    if (this.caughtUp) return false;
    const { complete, whole } = this.waiting();
    const early = this.early || (complete.length === 0 && whole.length >= EARLY_WORDS);
    return (early ? whole : complete).length === 0;
  }

  /** The text grew to `target`. Anything but an extension of what is shown restarts the line. */
  arrive(target: string, nowMs: number): void {
    if (target === this.target) return;
    // A loop that was resting or holding ran no frames; its next frame must not count that time.
    if (this.caughtUp || this.holding) this.lastFrameAt = nowMs;
    if (!target.startsWith(this.shown)) {
      this.end = 0;
      this.early = false;
    }
    this.target = target;
    this.sealed = false;
  }

  /** The stream closed this text (`textBoundary`, `done`, the block settling): all of it may play. */
  seal(): void {
    this.sealed = true;
  }

  /** One animation frame at `nowMs`: returns what is on screen after it. */
  frame(nowMs: number): string {
    const dt = Math.max(0, nowMs - this.lastFrameAt);
    this.lastFrameAt = Math.max(this.lastFrameAt, nowMs);
    const slowest = MAX_WORD_MS / BEAT_MS;
    const { complete, whole, completeAt } = this.waiting();
    if (!this.early && complete.length === 0 && whole.length >= EARLY_WORDS) this.early = true;
    const playable = this.early ? whole : complete;

    if (playable.length === 0) {
      // Holding. A paragraph that started early and is shown up to a finished sentence is done
      // being early: whatever comes next holds until it is complete.
      if (this.end >= completeAt) this.early = false;
      this.beats = slowest;
      return this.shown;
    }

    this.beats = Math.min(this.beats + dt / BEAT_MS, slowest);
    const stretch = this.early && complete.length === 0 && playable.length < LOW_WORDS;
    const everyBeats = stretch ? LOW_WORDS + 1 - playable.length : 1;
    // A frame within a quarter beat of the beat counts, so a 60 Hz screen never skips for jitter.
    if (this.beats < everyBeats - 0.25) return this.shown;
    this.end = playable[0];
    // Carry at most the quarter beat of jitter, so the next word keeps the beat rather than rushing.
    this.beats = Math.min(Math.max(0, this.beats - everyBeats), 0.25);
    return this.shown;
  }

  /**
   * The words past what is shown: `whole` ends every word followed by whitespace (and the last word
   * once the text is complete); `complete` is those inside the complete part of the text.
   */
  private waiting(): { complete: number[]; whole: number[]; completeAt: number } {
    const { target } = this;
    const completeAt = completeLength(target, this.sealed);
    const whole: number[] = [];
    for (let at = this.end + 1; at <= target.length; at += 1) {
      if (isSpace(target[at - 1])) continue;
      if (at === target.length ? completeAt === target.length : isSpace(target[at])) whole.push(at);
    }
    return { complete: whole.filter((end) => end <= completeAt), whole, completeAt };
  }
}

/** A static render runs no effects and has no frames: its text is shown whole. */
const canAnimate = () => typeof requestAnimationFrame === 'function';

export interface TextPlayback {
  /** What is on screen. */
  shown: string;
  /** Text has arrived that may not show yet, because its line is not complete. */
  holding: boolean;
}

/**
 * `target` held until each line is complete, then played back a whole word per beat, while `live`.
 * `sealed` says the stream has closed this text, so all of it may play.
 */
export function useTextPlayback(target: string, live: boolean, sealed = false): TextPlayback {
  // Created on the mounting render only, so a held line is never painted and then taken away.
  const [mounted] = useState(() => (live && canAnimate() ? new TextReveal(target, performance.now()) : null));
  const revealRef = useRef<TextReveal | null>(mounted);
  const [playback, setPlayback] = useState<TextPlayback>(() =>
    mounted ? { shown: mounted.shown, holding: mounted.holding } : { shown: target, holding: false },
  );
  const playbackRef = useRef(playback);

  useEffect(() => {
    const publish = (next: TextPlayback) => {
      if (next.shown === playbackRef.current.shown && next.holding === playbackRef.current.holding) return;
      playbackRef.current = next;
      setPlayback(next);
    };
    if (!live || !canAnimate()) {
      revealRef.current = null;
      publish({ shown: target, holding: false });
      return;
    }
    // Live again after settling: what is on screen is the whole text, so the reveal starts caught up.
    if (revealRef.current === null) revealRef.current = new TextReveal(target, performance.now(), { onScreen: true });
    const reveal = revealRef.current;
    reveal.arrive(target, performance.now());
    if (sealed) reveal.seal();
    publish({ shown: reveal.shown, holding: reveal.holding });
    let frame = 0;
    const step = (now: number) => {
      const shown = reveal.frame(now);
      publish({ shown, holding: reveal.holding });
      // Nothing to do until the next arrival or seal, which runs this effect again.
      frame = reveal.caughtUp || reveal.holding ? 0 : requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [target, live, sealed]);

  return live ? playback : { shown: target, holding: false };
}

/** {@link useTextPlayback}, for callers that only draw the text. */
export function useSmoothText(target: string, live: boolean, sealed = false): string {
  return useTextPlayback(target, live, sealed).shown;
}
